import * as THREE from 'three/webgpu';
import type { Viewer } from '../../../viewer/Viewer';
import { type DatumAxis, getDatumPlaneIdFromObject, type DatumPlane } from '../../../reference';
import { isFeatureCurve, isFeatureFace, isFeatureLine, isPlanarFeatureFace } from '../../../mesh/feature';
import {
  MoveAlongEntityConstraintKind,
  MoveAlongEntityReferenceType,
} from '../MoveComponentTypes';

/**
 * 沿实体移动参考解析结果。
 */
export interface MoveAlongEntityResolvedReference {
  /** 参考对象真实场景对象。 */
  object: THREE.Object3D;
  /** 参考对象 ID。 */
  objectId: string;
  /** 参考对象显示名称。 */
  label: string;
  /** 参考对象类型。 */
  referenceType: MoveAlongEntityReferenceType;
  /** 几何约束类型。 */
  constraintKind: MoveAlongEntityConstraintKind;
  /** 线性约束锚点。 */
  anchorWorld: THREE.Vector3;
  /** 线性约束方向；平面约束时为 `null`。 */
  directionWorld: THREE.Vector3 | null;
  /** 平面约束法向；线性约束时为 `null`。 */
  planeNormalWorld: THREE.Vector3 | null;
}

/**
 * 沿实体移动参考解析器。
 *
 * 设计说明：
 * 1. 统一收口“哪些对象可以作为沿实体参考”以及其几何求解参数；
 * 2. 明确只允许基准轴、基准面、特征直线、特征面四类对象；
 * 3. 曲线及非平面曲面不提供线性方向；平面 FeatureSurface 包装与普通平面使用同一语义。
 */
export class MoveAlongEntityReferenceResolver {
  /** Viewer 运行时。 */
  private readonly viewer: Viewer;

  /**
   * 创建沿实体移动参考解析器。
   *
   * @param viewer Viewer 运行时。
   */
  public constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /**
   * 判断对象是否可作为沿实体移动参考。
   *
   * @param object 待判断对象。
   * @returns 是否允许作为参考实体。
   */
  public canUseAsReference(object: THREE.Object3D | null | undefined): boolean {
    return this.resolve(object ?? null) !== null;
  }

  /**
   * 只读判断候选是否具备参考几何类型，不创建世界坐标向量或解析结果。
   * Hover 使用类型判断，写入与求解继续验证实际几何，退化轴线不能通过提交校验。
   * @param object 候选实体。
   * @returns 是否属于解析器支持的几何类型。
   */
  public isSupportedReferenceObject(object: THREE.Object3D | null | undefined): boolean {
    if (!object) return false;
    if (isFeatureFace(object)) return isPlanarFeatureFace(object);
    if (isFeatureLine(object)) return !isFeatureCurve(object);
    if (Reflect.get(object, 'isDatumAxis') === true) return true;
    const planeId = getDatumPlaneIdFromObject(object);
    return planeId !== null && this.viewer.getDatumPlane(planeId) != null;
  }

  /**
   * 解析当前对象的沿实体参考几何信息。
   *
   * @param object 待解析对象。
   * @returns 解析结果；不支持时返回 `null`。
   */
  public resolve(object: THREE.Object3D | null): MoveAlongEntityResolvedReference | null {
    if (!object || !this.isSupportedReferenceObject(object)) {
      return null;
    }

    const datumAxisReference = this.resolveDatumAxisReference(object);
    if (datumAxisReference) {
      return datumAxisReference;
    }

    const datumPlaneReference = this.resolveDatumPlaneReference(object);
    if (datumPlaneReference) {
      return datumPlaneReference;
    }

    const featureLineReference = this.resolveFeatureLineReference(object);
    if (featureLineReference) {
      return featureLineReference;
    }

    const featureFaceReference = this.resolveFeatureFaceReference(object);
    if (featureFaceReference) {
      return featureFaceReference;
    }
    return null;
  }

  /**
   * 解析基准轴参考。
   *
   * @param object 待解析对象。
   * @returns 解析结果；不支持时返回 `null`。
   */
  private resolveDatumAxisReference(object: THREE.Object3D): MoveAlongEntityResolvedReference | null {
    if (Reflect.get(object, 'isDatumAxis') !== true) {
      return null;
    }

    const datumAxis = object as DatumAxis;
    const directionWorld = datumAxis.getWorldDirection();
    const anchorWorld = datumAxis.getWorldMidpoint();
    if (!anchorWorld || directionWorld.lengthSq() <= 1e-12) {
      return null;
    }

    return {
      object: datumAxis,
      objectId: datumAxis.uuid,
      label: datumAxis.name,
      referenceType: MoveAlongEntityReferenceType.DatumAxis,
      constraintKind: MoveAlongEntityConstraintKind.LineLike,
      anchorWorld,
      directionWorld,
      planeNormalWorld: null,
    };
  }

  /**
   * 解析基准面参考。
   *
   * @param object 待解析对象。
   * @returns 解析结果；不支持时返回 `null`。
   */
  private resolveDatumPlaneReference(object: THREE.Object3D): MoveAlongEntityResolvedReference | null {
    const datumPlaneId = getDatumPlaneIdFromObject(object);
    if (datumPlaneId === null) {
      return null;
    }

    const datumPlane = this.viewer.getDatumPlane(datumPlaneId) as DatumPlane | null;
    if (!datumPlane) {
      return null;
    }

    return {
      object: datumPlane.getObject3D(),
      objectId: datumPlane.getObject3D().uuid,
      label: datumPlane.name,
      referenceType: MoveAlongEntityReferenceType.DatumPlane,
      constraintKind: MoveAlongEntityConstraintKind.PlaneLike,
      anchorWorld: datumPlane.originWorld.clone(),
      directionWorld: null,
      planeNormalWorld: datumPlane.normal.clone().normalize(),
    };
  }

  /**
   * 解析特征直线参考。
   *
   * @param object 待解析对象。
   * @returns 解析结果；不支持时返回 `null`。
   */
  private resolveFeatureLineReference(object: THREE.Object3D): MoveAlongEntityResolvedReference | null {
    if (!isFeatureLine(object) || isFeatureCurve(object)) {
      return null;
    }

    const directionWorld = object.getWorldDirection();
    const anchorWorld = object.getWorldMidpoint();
    if (!anchorWorld || !directionWorld || directionWorld.lengthSq() <= 1e-12) {
      return null;
    }

    return {
      object,
      objectId: object.uuid,
      label: object.name,
      referenceType: MoveAlongEntityReferenceType.FeatureLine,
      constraintKind: MoveAlongEntityConstraintKind.LineLike,
      anchorWorld,
      directionWorld,
      planeNormalWorld: null,
    };
  }

  /**
   * 解析特征面参考。
   *
   * @param object 待解析对象。
   * @returns 解析结果；不支持时返回 `null`。
   */
  private resolveFeatureFaceReference(object: THREE.Object3D): MoveAlongEntityResolvedReference | null {
    if (!isPlanarFeatureFace(object)) {
      return null;
    }

    const plane = object.getWorldPlane();
    if (plane.normal.lengthSq() <= 1e-12) {
      return null;
    }

    return {
      object,
      objectId: object.uuid,
      label: object.name,
      referenceType: MoveAlongEntityReferenceType.FeatureFace,
      constraintKind: MoveAlongEntityConstraintKind.PlaneLike,
      anchorWorld: plane.point.clone(),
      directionWorld: null,
      planeNormalWorld: plane.normal.clone().normalize(),
    };
  }
}
